import { ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  GitMerge,
  ChevronRight,
  Database,
  Settings,
  Play,
  Star,
  Check,
} from "lucide-react";
import { useMdmRunStore } from "./store/useMdmRunStore";
import { Sparkles } from "lucide-react";

interface MdmLayoutProps {
  children: ReactNode;
  currentScreen:
    | "source-selection"
    | "rule-builder"
    | "golden-records"
    | "run-status";
}

const STEPS: {
  id: MdmLayoutProps["currentScreen"];
  label: string;
  icon: React.ComponentType<{ size?: number }>;
  route: (pid: string, rid: string, entityType?: string | null) => string;
}[] = [
  {
    id: "source-selection",
    label: "Connect / Upload",
    icon: Database,
    route: (p, r) =>
      `/projects/${p}/agents/mdm-agent/runs/${r}/source-selection`,
  },
  {
    id: "rule-builder",
    label: "Match Config",
    icon: Settings,
    route: (p, r, et) => {
      const base = `/projects/${p}/agents/mdm-agent/runs/${r}/config`;
      return et ? `${base}?entity_type=${encodeURIComponent(et)}` : base;
    },
  },
  {
    id: "run-status",
    label: "Run & Review",
    icon: Play,
    route: (p, r) => `/projects/${p}/agents/mdm-agent/runs/${r}/status`,
  },
  {
    id: "golden-records",
    label: "Golden Records",
    icon: Star,
    route: (p, r) => `/projects/${p}/agents/mdm-agent/runs/${r}/golden-records`,
  },
];

function StepIndicator({
  currentScreen,
  projectId,
  runId,
  lastCompletedRunId,
  entityType,
}: {
  currentScreen: MdmLayoutProps["currentScreen"];
  projectId: string | undefined;
  runId: string | undefined;
  lastCompletedRunId: string | null;
  entityType?: string | null;
}) {
  const navigate = useNavigate();
  const currentIdx = STEPS.findIndex((s) => s.id === currentScreen);

  return (
    <div className="flex items-center justify-center gap-1 py-4">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const isActive = i === currentIdx;
        const isDone = i < currentIdx;
        // Golden Records is always clickable when any run has completed
        const isGoldenRecords = s.id === "golden-records";
        const goldenRunId = lastCompletedRunId ?? runId;
        const isClickable =
          (isDone && projectId && runId) ||
          (isGoldenRecords && projectId && goldenRunId);
        return (
          <div key={s.id} className="flex items-center gap-1">
            <button
              data-testid="mdm-step-nav"
              data-step={s.id}
              onClick={() =>
                isClickable &&
                navigate(
                  s.route(
                    projectId!,
                    isGoldenRecords && goldenRunId ? goldenRunId : runId!,
                    entityType,
                  ),
                )
              }
              disabled={!isClickable && !isActive}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-300 ${
                isActive
                  ? "bg-brand-500/20 text-brand-500 border border-brand-500/40"
                  : isGoldenRecords &&
                      lastCompletedRunId &&
                      !isDone &&
                      !isActive
                    ? "bg-warning/10 text-warning/80 border border-warning/20 hover:bg-warning/15 hover:text-warning cursor-pointer"
                    : isDone
                      ? "bg-brand-500/10 text-brand-500/60 border border-brand-500/20 hover:bg-brand-500/15 hover:text-brand-500 cursor-pointer"
                      : "bg-[var(--overlay-faint)] text-[var(--text-primary)] border border-[var(--border-subtle)] cursor-not-allowed"
              }`}
            >
              {isDone ? (
                <Check size={12} className="text-indigo-400" />
              ) : (
                <Icon size={12} />
              )}
              <span className="hidden sm:inline">{s.label}</span>
            </button>
            {i < STEPS.length - 1 && (
              <div
                className={`w-6 h-px ${isDone ? "bg-brand-500/40" : "bg-[var(--overlay-medium)]"}`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export function MdmLayout({ children, currentScreen }: MdmLayoutProps) {
  const navigate = useNavigate();
  const { projectId, runId } = useParams<{
    projectId?: string;
    runId?: string;
  }>();
  const {
    runName,
    runStatus,
    entityType,
    pendingReviewCount,
    lastCompletedRunId,
  } = useMdmRunStore();
  const goldenRunId =
    lastCompletedRunId ?? (runStatus === "completed" ? runId : null);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Run context header */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-[var(--border-muted)] bg-[var(--surface-dark)]/75 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-brand-500">
            <GitMerge size={16} />
            Master Data Management
          </span>
          {runName && (
            <>
              <ChevronRight size={14} className="text-[var(--text-primary)]" />
              <span className="text-sm text-white/70">{runName}</span>
            </>
          )}
          {runStatus && (
            <span
              data-testid="mdm-run-status-badge"
              data-status={runStatus}
              className={`ztd-badge ${
                runStatus === "completed"
                  ? "ztd-badge-success"
                  : runStatus === "running"
                    ? "ztd-badge-info"
                    : runStatus === "failed"
                      ? "ztd-badge-critical"
                      : runStatus === "cancelled"
                        ? "ztd-badge-warning"
                        : "ztd-badge"
              }`}
            >
              {runStatus}
            </span>
          )}
          {entityType && (
            <span className="ztd-badge ztd-badge-info text-[10px] uppercase tracking-wide">
              {entityType}
            </span>
          )}
        </div>

        {/* Golden Records shortcut — visible from any screen once a run has completed */}
        <div className="flex items-center gap-2">
          {goldenRunId && projectId && currentScreen !== "golden-records" && (
            <button
              onClick={() =>
                navigate(
                  `/projects/${projectId}/agents/mdm-agent/runs/${goldenRunId}/golden-records`,
                )
              }
              className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-full font-medium border border-warning/30 bg-warning/10 text-warning transition hover:bg-warning/20"
            >
              <Sparkles size={11} />
              Golden Records
            </button>
          )}

          {/* HITL badge */}
          {pendingReviewCount > 0 && runId && (
            <button
              onClick={() =>
                projectId &&
                runId &&
                navigate(
                  `/projects/${projectId}/agents/mdm-agent/runs/${runId}/review`,
                )
              }
              className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-full font-medium border border-warning/30 bg-warning/20 text-warning transition hover:bg-warning/30"
            >
              {pendingReviewCount} pairs await review
            </button>
          )}
        </div>
      </div>

      {/* Profiler-style step indicator */}
      <div className="border-b border-[var(--border-subtle)] bg-[var(--surface-dark)]/50">
        <StepIndicator
          currentScreen={currentScreen}
          projectId={projectId}
          runId={runId}
          lastCompletedRunId={lastCompletedRunId}
          entityType={entityType}
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto custom-scrollbar relative z-10">
        {children}
      </div>
    </div>
  );
}
